import { createSelector } from 'reselect';
import isEqual from 'lodash/isEqual';
import { RootState } from '@store/index';
import {
  getLineItemSelector,
  getSubscriptionCycleSelector,
  getSubscriptionOptionsSelector,
} from '@modules/subscriptions/core/selectors';
import { getUpdatedSubscriptionOptions } from '@modules/settings/core/selectors';
import { isNewSuiteProduct } from '@shared/constants/common';

const getPending = (state: RootState) => state.products.pending;

const getProducts = (state: RootState) => state.products.data;

const getError = (state: RootState) => state.products.error;

export const getProductsSelector = createSelector(getProducts, (products) => products);

export const getPendingSelector = createSelector(getPending, (pending) => pending);

export const getErrorSelector = createSelector(getError, (error) => error);

export const getProductSelector = createSelector(getProducts, (products) => products.product);

export const getIsCurrentProductNewSuiteSelector = createSelector(getProductSelector, (product) =>
  isNewSuiteProduct(product),
);

export const getProductOptionsSelector = createSelector(
  getProducts,
  (products) => products.product.options,
);

export const getVariantsSelector = createSelector(
  getProducts,
  (products) => products?.product?.variants || [],
);

export const getVariantSelector = createSelector(
  [getVariantsSelector, getLineItemSelector],
  (variants, lineItem) =>
    variants?.find((variant: any) => parseInt(variant.id) === lineItem?.variantShopifyId),
);

export const getVariantProductInfoSelector = createSelector(
  [getVariantSelector],
  (variant) => variant?.metafields?.product_info ?? null,
);

export const getUpdatedVariantSelector = createSelector(
  [getUpdatedSubscriptionOptions, getVariantsSelector],
  (updatedSubscriptionOptions, variants) =>
    variants.find(
      (variant: any) =>
        updatedSubscriptionOptions?.cycle === variant.selectedOptions?.cycle &&
        updatedSubscriptionOptions?.pension === variant.selectedOptions?.pension &&
        isEqual(updatedSubscriptionOptions?.recipes, variant.selectedOptions.recipes),
    ),
);

export const getIsDisabled14DaysCycleSelector = createSelector(
  [getVariantsSelector, getUpdatedSubscriptionOptions],
  (variants, options) => {
    const pension = options?.pension;
    const recipes = options?.recipes;

    return !variants.some(
      (variant: any) =>
        variant.selectedOptions?.cycle === 14 &&
        (pension == null || variant.selectedOptions?.pension === pension) &&
        (recipes == null || isEqual(variant.selectedOptions?.recipes, recipes)),
    );
  },
);

// Distinct delivery cycles (in days) offered by the current product for the
// currently-selected pension + recipes, sorted ascending. This replaces the
// hardcoded 14/28 picker: the options are now whatever the Shopify variants
// expose. Falls back to an empty array while the product is still loading, so
// callers can decide their own default.
//
// Mirrors getIsDisabled14DaysCycleSelector's matching rules (pension + recipes,
// no availability filter) so a product that only ships 14/28 yields exactly the
// same two options as before.
export const getAvailableCyclesSelector = createSelector(
  [getVariantsSelector, getUpdatedSubscriptionOptions],
  (variants, options): number[] => {
    if (!Array.isArray(variants)) return [];

    const pension = options?.pension;
    const recipes = options?.recipes;

    const cycles = variants
      .filter(
        (variant: any) =>
          (pension == null || variant.selectedOptions?.pension === pension) &&
          (recipes == null || isEqual(variant.selectedOptions?.recipes, recipes)),
      )
      .map((variant: any) => variant.selectedOptions?.cycle)
      .filter((cycle: any): cycle is number => typeof cycle === 'number' && Number.isFinite(cycle));

    return Array.from(new Set(cycles)).sort((a, b) => a - b);
  },
);

// The dashboard gates on its own flag (custom.available_in_cd) so the
// storefront's custom.available_for_sale can differ. During rollout the CD flag
// may be missing on some variants, so fall back to the storefront flag when the
// CD one is absent (empty/undefined). Both arrive as strings ("true"/"false").
const resolveAvailableForSale = (variant: any): string | undefined => {
  const cd = variant?.metafields?.available_in_cd;
  const cdStr = cd == null ? '' : String(cd).trim();
  if (cdStr.length > 0) return cdStr;
  return variant?.metafields?.available_for_sale;
};

const isVariantAvailableForSale = (variant: any): boolean =>
  String(resolveAvailableForSale(variant)).trim().toLowerCase() === 'true';

const isVariantExplicitlyNotForSale = (variant: any): boolean =>
  String(resolveAvailableForSale(variant)).trim().toLowerCase() === 'false';

export const getIsDisabledHalfPensionSelector = createSelector(
  [getVariantsSelector, getUpdatedSubscriptionOptions, getSubscriptionCycleSelector],
  (variants, options, subscriptionCycle) => {
    if (!Array.isArray(variants)) return true;

    const currentCycle = options?.cycle ?? subscriptionCycle;

    return !variants.some(
      (variant: any) =>
        variant?.selectedOptions?.pension === 'H' &&
        (currentCycle == null || variant?.selectedOptions?.cycle === currentCycle) &&
        isVariantAvailableForSale(variant),
    );
  },
);

// True when the customer's CURRENT plan (their subscription's cycle + pension +
// recipes, parsed from the line SKU) can no longer be purchased from the current
// product — either no variant matches it at all, or every matching variant is
// explicitly flagged not-for-sale. Drives the "please update your delivery
// cycle" notice on the Mahlzeiten page and in the recalculation popup.
//
// Conservative on purpose:
//  - returns false while variants are still loading (never nag prematurely),
//  - a MISSING availability flag is NOT treated as unavailable (only an explicit
//    "false" is), so we don't flag customers whose catalog data simply omits it.
//    Availability resolves available_in_cd first, then falls back to
//    available_for_sale (see resolveAvailableForSale).
export const getIsCurrentPlanUnavailableSelector = createSelector(
  [getVariantsSelector, getSubscriptionOptionsSelector],
  (variants, options) => {
    if (!Array.isArray(variants) || variants.length === 0) return false;
    if (options?.cycle == null || options?.pension == null || options?.recipes == null) {
      return false;
    }

    const matching = variants.filter(
      (variant: any) =>
        variant.selectedOptions?.cycle === options.cycle &&
        variant.selectedOptions?.pension === options.pension &&
        isEqual(variant.selectedOptions?.recipes, options.recipes),
    );

    // Plan no longer exists in the catalog at all.
    if (matching.length === 0) return true;

    // Plan still exists: unavailable only if none are for sale AND at least one
    // matching variant is explicitly marked not-for-sale.
    const anyForSale = matching.some((variant: any) => isVariantAvailableForSale(variant));
    const anyExplicitlyUnavailable = matching.some((variant: any) =>
      isVariantExplicitlyNotForSale(variant),
    );

    return !anyForSale && anyExplicitlyUnavailable;
  },
);

// The first available-for-sale plan to auto-preselect when the customer's
// current plan is no longer purchasable, so the update-subscription banner opens
// with a ready-to-save change. Returned as full options ({ recipes, pension,
// cycle }) so the banner can open even when the current recipes/pension combo is
// itself discontinued.
//
// Preference order:
//   1. Keep the current recipes + pension, pick the smallest for-sale cycle
//      (i.e. just switch the delivery interval — the common migration case).
//   2. Otherwise pick any for-sale variant, preferring Vollpension then the
//      smallest cycle.
// Returns null only when the product has no for-sale variant at all.
export const getFirstAvailablePlanSelector = createSelector(
  [getVariantsSelector, getSubscriptionOptionsSelector],
  (variants, options): { recipes: string[]; pension: string; cycle: number } | null => {
    if (!Array.isArray(variants) || variants.length === 0) return null;

    const cycleOf = (variant: any): number => variant?.selectedOptions?.cycle;
    const hasValidCycle = (variant: any): boolean =>
      typeof cycleOf(variant) === 'number' && Number.isFinite(cycleOf(variant));

    const forSale = variants.filter(
      (variant: any) => isVariantAvailableForSale(variant) && hasValidCycle(variant),
    );
    if (forSale.length === 0) return null;

    // 1) Keep current recipes + pension when a for-sale cycle exists for them.
    if (options?.pension != null && options?.recipes != null) {
      const sameCombo = forSale.filter(
        (variant: any) =>
          variant.selectedOptions?.pension === options.pension &&
          isEqual(variant.selectedOptions?.recipes, options.recipes),
      );
      if (sameCombo.length > 0) {
        return {
          recipes: options.recipes,
          pension: options.pension,
          cycle: Math.min(...sameCombo.map(cycleOf)),
        };
      }
    }

    // 2) Fall back to any for-sale variant: Vollpension first, then smallest cycle.
    const best = [...forSale].sort((a: any, b: any) => {
      const rankA = a.selectedOptions?.pension === 'V' ? 0 : 1;
      const rankB = b.selectedOptions?.pension === 'V' ? 0 : 1;
      if (rankA !== rankB) return rankA - rankB;
      return cycleOf(a) - cycleOf(b);
    })[0];

    return {
      recipes: best.selectedOptions?.recipes ?? [],
      pension: best.selectedOptions?.pension,
      cycle: cycleOf(best),
    };
  },
);

export const getDisabledRecipesSelector = createSelector([getVariantsSelector], (variants) => []);
